/**
 * The agent conversation, written down in full: every line this app sends the
 * agent, everything the agent sends back, and what the app then did with it.
 *
 * `lib/debug` already traces the transport, but it traces it *compactly* —
 * previews of the text, keys instead of values — because those lines are read
 * while watching the room connect, and a screen digest pasted into the console
 * buries the conversation. That is the wrong trade when the question is "what
 * exactly did we send, and what exactly came back". This log answers that one:
 * a short summary line per interaction, with the whole payload in a collapsed
 * group under it, and a rolling buffer that can be dumped as JSON afterwards —
 * a console scrollback does not survive a reload, and an agent bug usually
 * needs the turn *before* the one still on screen.
 *
 *   out  turn                a question, as the wire frame carries it
 *   out  report              an `[api_result]`, verbatim
 *   out  turn/report failed  the send that did not leave
 *   in   envelope            `{ reply, data, actions }`, raw
 *   in   envelope.parsed     the same envelope as the executor reads it
 *   in   agentTranscript     finished agent speech
 *   in   userTranscript      finished user speech, as the worker heard it
 *   in   chat / system       a plain reply, an over-budget notice
 *   app  executed / dropped  what the envelope did, or why nothing did
 *
 * On in development. In any build it follows `localStorage['ki.agentLog']`
 * (`'on'` / `'off'`), so a staging session can be turned up without a rebuild:
 *
 *   __kiAgentLog.on()        // and off()
 *   __kiAgentLog.dump()      // the entries, oldest first
 *   __kiAgentLog.json()      // the same, as JSON to paste into a bug report
 *   __kiAgentLog.clear()
 *
 * Nothing is recorded while it is off: the payloads carry the reader's
 * questions and their screen's data, and those do not belong in the memory of
 * a tab that nobody is debugging.
 */

/** Where the on/off choice is kept, so it survives the reload it is needed across. */
export const AGENT_LOG_KEY = 'ki.agentLog';

/** Entries kept in memory. A long voice session is a few dozen turns; this is many. */
const MAX_ENTRIES = 300;

/** A payload larger than this is summarised rather than kept, to bound the buffer. */
const MAX_DETAIL_CHARS = 120000;

const GLOBAL_NAME = '__kiAgentLog';

const STYLE_OUT = 'color:#0b8f5a;font-weight:600';
const STYLE_IN = 'color:#6d5efc;font-weight:600';
const STYLE_APP = 'color:#8a8f98;font-weight:600';

const entries = [];
let seq = 0;
let enabled = null;

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

function readStored() {
  try {
    return window.localStorage.getItem(AGENT_LOG_KEY);
  } catch {
    /* Private mode, or storage blocked: fall back to the build default. */
    return null;
  }
}

/** Whether interactions are being recorded: the stored choice, else dev-only. */
export function agentLogEnabled() {
  if (enabled === null) {
    const stored = typeof window === 'undefined' ? null : readStored();
    if (stored === 'on' || stored === 'off') enabled = stored === 'on';
    else enabled = process.env.NODE_ENV !== 'production';
  }
  return enabled;
}

/** Turn recording on or off, for this tab and the next. */
export function setAgentLogEnabled(on) {
  enabled = Boolean(on);
  try {
    window.localStorage.setItem(AGENT_LOG_KEY, enabled ? 'on' : 'off');
  } catch {
    /* Not persisting is survivable; this tab still honours the switch. */
  }
  return enabled;
}

/**
 * A detached copy of the payload, so an entry read ten turns later still shows
 * what was sent rather than whatever the store has since done to that object.
 * Anything circular or outsized is kept as a note instead of dropped silently.
 */
function snapshot(value) {
  if (value === undefined) return null;
  let json;
  try {
    json = JSON.stringify(value);
  } catch (err) {
    return { _unserializable: String(err?.message || err) };
  }
  if (json === undefined) return String(value);
  if (json.length > MAX_DETAIL_CHARS) {
    return { _tooLarge: json.length, _preview: `${json.slice(0, 2000)}…` };
  }
  return JSON.parse(json);
}

const clip = (s, max) => (s.length > max ? `${s.slice(0, max - 1)}…` : s);

/** The one-line headline: enough to find the entry worth expanding. */
function summarize(kind, detail) {
  if (typeof detail === 'string') return clip(detail.replace(/\s+/g, ' ').trim(), 120);
  if (!isPlainObject(detail)) return '';
  if (typeof detail.text === 'string') return clip(detail.text.replace(/\s+/g, ' ').trim(), 120);
  if (kind === 'envelope' || kind === 'envelope.parsed') {
    const reply = typeof detail.reply === 'string' ? clip(detail.reply.replace(/\s+/g, ' ').trim(), 80) : '';
    const commands = Array.isArray(detail.commands)
      ? detail.commands.map((c) => c?.type).filter(Boolean)
      : Object.keys(isPlainObject(detail.data) ? detail.data : {});
    const parts = [];
    if (reply) parts.push(`"${reply}"`);
    if (commands.length) parts.push(commands.join(', '));
    return parts.join(' · ') || '(no reply, no commands)';
  }
  const keys = Object.keys(detail);
  return keys.length ? clip(keys.map((k) => `${k}=${oneValue(detail[k])}`).join(' '), 140) : '';
}

/** A field as it reads in a headline; objects stay as their shape, not their contents. */
function oneValue(v) {
  if (v == null) return String(v);
  if (Array.isArray(v)) return `[${v.length}]`;
  if (typeof v === 'object') return `{${Object.keys(v).length}}`;
  return clip(String(v).replace(/\s+/g, ' '), 40);
}

/**
 * Record one interaction and print it.
 *
 * `dir` is who spoke: `out` this app, `in` the agent, `app` neither (what the
 * app did with what it heard). The detail is printed whole — this log exists
 * because the compact trace is not enough.
 */
function log(dir, kind, detail) {
  if (!agentLogEnabled()) return null;
  seq += 1;
  const entry = {
    seq,
    at: new Date().toISOString(),
    t: Date.now(),
    dir,
    kind,
    summary: summarize(kind, detail),
    detail: snapshot(detail),
  };
  entries.push(entry);
  while (entries.length > MAX_ENTRIES) entries.shift();

  const arrow = dir === 'out' ? '→' : dir === 'in' ? '←' : '·';
  const style = dir === 'out' ? STYLE_OUT : dir === 'in' ? STYLE_IN : STYLE_APP;
  const head = `%c[agent ${arrow} ${kind}]%c ${entry.summary}`;
  /* Collapsed: the headline is what is scanned, the payload is what is opened.
     A console without groups (jsdom, a check script) still prints both. */
  if (typeof console.groupCollapsed === 'function' && typeof console.groupEnd === 'function') {
    console.groupCollapsed(head, style, 'color:inherit;font-weight:400');
    console.log(entry.detail);
    console.groupEnd();
  } else {
    console.log(head, style, 'color:inherit;font-weight:400', entry.detail);
  }
  return entry;
}

/** A line this app sent the agent: a question, a report, or one that failed. */
export const logAgentOut = (kind, detail) => log('out', kind, detail);

/** Something the agent sent: an envelope, speech, a chat line, a notice. */
export const logAgentIn = (kind, detail) => log('in', kind, detail);

/** What the app made of it: an envelope executed, a duplicate dropped. */
export const logAgentApp = (kind, detail) => log('app', kind, detail);

/** The recorded interactions, oldest first. A copy: dumping cannot disturb the log. */
export function agentLogEntries() {
  return entries.slice();
}

/** The log as JSON, for pasting into a bug report. */
export function agentLogJson() {
  return JSON.stringify(entries, null, 2);
}

/** Forget everything recorded so far. */
export function clearAgentLog() {
  entries.length = 0;
  return true;
}

/* `window.__kiAgentLog`, in every build: the switch is the point, and a
   production tab that is misbehaving is exactly where it gets thrown. Off by
   default there, so nothing is held until someone asks for it. */
if (typeof window !== 'undefined') {
  window[GLOBAL_NAME] = Object.freeze({
    on: () => setAgentLogEnabled(true),
    off: () => setAgentLogEnabled(false),
    get enabled() {
      return agentLogEnabled();
    },
    dump: agentLogEntries,
    json: agentLogJson,
    clear: clearAgentLog,
  });
}
